import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

export type ApiRequest_SendPaymentLinkEmail = {
  tenantId: number;
  paymentLinks: {
    achUrl: string;
    cardUrl: string;
    baseAmount?: number;
  };
  tenantEmail: string;
  tenantName: string;
};

export type ApiResponse_SendPaymentLinkEmail = {
  success: boolean;
  message: string;
};

// This endpoint is accessible to property managers
// The backend uses @UseGuards(UserTypeGuard) with @Roles('property_manager')
const METHOD = 'POST';
const ENDPOINT = 'payments/send-payment-link-email';
export const MUTATION_KEY = () => getQueryKey(METHOD, ENDPOINT, {});

export const useSendPaymentLinkEmail = (
  options?: UseMutationOptions<
    ApiResponse_SendPaymentLinkEmail,
    AxiosError,
    ApiRequest_SendPaymentLinkEmail
  >
) => {
  const { session } = useAuth();

  return useMutation<ApiResponse_SendPaymentLinkEmail, AxiosError, ApiRequest_SendPaymentLinkEmail>(
    {
      mutationKey: MUTATION_KEY(),
      mutationFn: async (data: ApiRequest_SendPaymentLinkEmail) => {
        try {
          const response = await handleApiCall<
            ApiResponse_SendPaymentLinkEmail,
            ApiRequest_SendPaymentLinkEmail
          >(session, METHOD, getResolvedEndpoint(ENDPOINT, {}), data);
          return response || { success: true, message: 'Payment links email sent successfully' };
        } catch (err) {
          enqueueSnackbar('Failed to send payment links email.', { variant: 'error' });
          throw err;
        }
      },
      ...options,
    }
  );
};
